import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { RefreshCw, X } from 'lucide-react';

/**
 * "New version available" toast — surfaces the waiting service worker
 * fired by /src/index.js as a `lumera:sw-update-ready` event. Clicking
 * "Update now" posts {type:'SKIP_WAITING'} so the worker activates,
 * which triggers `controllerchange` and a page reload.
 */
const UpdateAvailableToast = () => {
  const [waiting, setWaiting] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => setWaiting(e.detail?.worker || null);
    window.addEventListener('lumera:sw-update-ready', onUpdate);
    return () => window.removeEventListener('lumera:sw-update-ready', onUpdate);
  }, []);

  if (!waiting || dismissed) return null;

  const refresh = () => {
    try {
      // Tell the waiting worker to take over, then reload to pick up new assets.
      waiting.postMessage({ type: 'SKIP_WAITING' });
      // Give the worker a beat to activate before reloading.
      setTimeout(() => window.location.reload(), 300);
    } catch (_) {
      window.location.reload();
    }
  };

  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-50 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
      data-testid="update-available-toast"
    >
      <div className="rounded-xl border border-emerald-300/60 dark:border-emerald-700/60 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-xl p-3 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
          <RefreshCw className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 dark:text-white">
            New version available
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Refresh to load the latest features and bug fixes.
          </p>
          <div className="flex items-center gap-1.5 mt-2">
            <Button
              size="sm"
              onClick={refresh}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs"
              data-testid="update-available-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Refresh
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDismissed(true)}
              className="h-8 px-2 text-xs text-muted-foreground"
              data-testid="update-available-later-btn"
            >
              Later
            </Button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Close"
          className="text-muted-foreground hover:text-foreground p-1 -mr-1"
          data-testid="update-available-close-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default UpdateAvailableToast;
